import clsx from 'clsx';
import { useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useNavigation } from '@/contexts/navigation';

import type {
  FagmoblerInventory,
  FagmoblerPublicOmniumProduct,
} from '@fagmobler/types';
import { Button, Sticker, StockIndicators } from '@fagmobler/ui';
import { useCart } from '@/components/cart';
import {
  useStores,
  useStoresByRegion,
  useStoresInRegion,
  useStoresWithStock,
} from '@/components/store';

import type { OmniumStoreWithStock } from '@/components/store';
import { getStoreIsWebshop } from '@fagmobler/omnium';

export type StockProps = {
  product: FagmoblerPublicOmniumProduct;
  inventory?: FagmoblerInventory;
  children?: any;
};

export function Stock({ product, inventory = {}, children }: StockProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { setStore } = useCart();
  const {
    current: {
      data: {
        store: rawSelectedStore = null,
        region: selectedRegion = null,
      } = {},
      refetch: refetchStores,
    },
    stores: { data: rawStores = [] },
  } = useStores();

  const stores = useStoresWithStock(rawStores, inventory);
  const { setShowStoreStock, setShowStores } = useNavigation();

  const [selectedStore] = useStoresWithStock(
    rawSelectedStore ? [rawSelectedStore] : [],
    inventory
  );

  const storesByRegion = useStoresByRegion<OmniumStoreWithStock>(stores);
  const storesInRegion = useStoresInRegion(storesByRegion, selectedRegion);
  const currentStock =
    selectedStore?.id && typeof inventory[product.skuId] !== 'undefined'
      ? inventory[product.skuId][selectedStore.id]
      : null;

  const shopsWithInventory = stores.filter(
    (store) =>
      store &&
      store.isPrimaryStore &&
      store.id &&
      inventory[product.skuId] &&
      inventory[product.skuId][store.id] &&
      (inventory[product.skuId][store.id].stock > 0 ||
        inventory[product.skuId][store.id].display > 0)
  );

  return (
    <div className="print:hidden">
      {selectedStore && (
        <div className={clsx('bg-neutral-20 p-5 my-5')}>
          <div className="flex justify-between items-end">
            <div>
              <Sticker size="sm" className="uppercase">
                Valgt butikk
              </Sticker>
              <h3 className={clsx('hd-md', 'mt-2')}>{selectedStore.name}</h3>
              <p>
                {selectedStore.address?.streetName}{' '}
                {selectedStore.address?.streetNumber}
                {', '}
                {selectedStore.address?.zipcode} {selectedStore.address?.city}{' '}
              </p>
              <div className={clsx('my-2')}>
                <StockIndicators stock={currentStock} />
              </div>
            </div>
            <div>
              <Button
                onClick={() =>
                  setShowStoreStock({
                    visible: true,
                    inventory: inventory,
                    storeStockContext: product,
                  })
                }
              >
                Endre butikk
              </Button>
            </div>
          </div>
          <div className="mt-5">{children}</div>
        </div>
      )}
      {!selectedStore && storesInRegion && (
        <>
          <div className={clsx('bg-neutral-20 p-5 my-5')}>{children}</div>
          <h3 className="mt-5 hd-base mb-1">
            Tilgjengelig i {shopsWithInventory.length} butikker
          </h3>
          <p className="body-xs">
            Hos Fagmøbler kjøper du alltid fra en butikk. Butikkene har
            forskjellige leveringsvalg, men alltid samme gode pris!
          </p>
          <ul className="space-y-2 my-2 mb-5">
            {storesInRegion
              .filter((store) => getStoreIsWebshop(store))
              .filter((store) =>
                shopsWithInventory.find((s) => s.id === store.id)
              )
              .map((store) => (
                <li
                  key={store.id}
                  className={clsx(
                    'relative',
                    'xl:flex',
                    'xl:justify-between',
                    'items-center',
                    'p-3',
                    'bg-neutral-20'
                  )}
                >
                  <div>
                    <p className={clsx('hd-base', 'mb-2')}>{store.name}</p>

                    <StockIndicators stock={store.stock[product.skuId]} />
                  </div>
                  <div
                    className={clsx(
                      'absolute',
                      'xl:static',
                      'top-0',
                      'right-0',
                      'flex',
                      'h-full',
                      'items-center',
                      'whitespace-nowrap',
                      'ml-2'
                    )}
                  >
                    <Button
                      onClick={async () => {
                        await setStore.mutateAsync(store.id);
                        await refetchStores();
                      }}
                      variant="primary"
                    >
                      Velg
                    </Button>
                  </div>
                </li>
              ))}
          </ul>
          <Button
            onClick={() =>
              setShowStoreStock({
                visible: true,
                inventory,
                storeStockContext: product,
              })
            }
          >
            Se alle butikker
            <ArrowRightIcon className={clsx('w-3', 'h-3', 'ml-2')} />
          </Button>
        </>
      )}
    </div>
  );
}

export default Stock;
